import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { FeatureFlag } from '@app/enums';
import { Employee } from '@app/models/employee/employee.model';
import { AuthService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { BehaviorSubject } from 'rxjs';
import { finalize, takeWhile } from 'rxjs/operators';
import { ImportType, ImportTypeMap } from '../../types/import.types';

const IMPORT_CONFIGS: ImportTypeMap = {
    FILE: {
        icon: 'file',
        routerLink: ['/company', 'import', 'employees'],
    },
    INTEGRATIONS: {
        icon: 'link',
        routerLink: ['/company', 'import', 'integrations'],
    },
};

@Injectable({
    providedIn: 'root',
})
export class ImportService {
    public readonly IMPORT_CONFIGS: ImportTypeMap = IMPORT_CONFIGS;

    private isLoading$: BehaviorSubject<boolean> = new BehaviorSubject(true);
    private availableImportTypes: Set<ImportType> = new Set();

    constructor(
        private featureService: FeatureService,
        private authService: AuthService,
        private router: Router
    ) {
        this.initializeAvailableImportTypes();
    }

    /**
     * @returns array of available import types once resolved.
     */
    async getAvailableImportTypes(): Promise<ImportType[]> {
        return new Promise((resolve) => {
            this.isLoading$
                .pipe(
                    takeWhile((val) => val),
                    finalize(() => resolve([...this.availableImportTypes]))
                )
                .subscribe();
        });
    }

    hasImportType(type: ImportType): boolean {
        return this.availableImportTypes.has(type);
    }

    /**
     * Launches flow for specified import type
     * @param type The import type that was selected (FILE, INTEGRATIONS, etc.)
     * @param $event The DOM event that triggered the handler. Optional - can be used for custom onClick behaviour
     */
    handleImportTypeSelected(type: ImportType, $event?: Event): void {
        const { routerLink, onClick } = this.IMPORT_CONFIGS[type];

        if (routerLink) {
            this.router.navigate(routerLink);
            return;
        }

        if (onClick) {
            onClick($event);
        }
    }

    /**
     * Checks which import types are available for the user. Additional integration rules can be added here
     */
    private initializeAvailableImportTypes(): void {
        const canImport = this.authService.can(Employee.importPermission);

        if (!canImport) {
            return;
        }
        // Import via file
        this.availableImportTypes.add('FILE');

        // Import via integrations
        this.featureService.has(FeatureFlag.employeeImportIntegrations).then((canAccessIntegrations) => {
            if (canAccessIntegrations) {
                this.availableImportTypes.add('INTEGRATIONS');
            }
            this.isLoading$.next(false);
        });
    }
}
